import { ReactNode, createContext } from "react";
import useAluno, {
    AlunoType,
    CreateAlunoType,
    EditAlunoType,
    AlunoDetalhadoType,
    AlunoRelatorioRequest,
    CopyTransferAlunosRequest,
    CopyTransferAlunosResponse,
    RemoveAlunoFromContratoResponse
} from "./hooks/useAluno";
import { DeleteGuardResult } from "../types/deleteGuard";

interface AlunoContextProviderProps {
    children: ReactNode;
}

export interface AlunoContextData {
    createAluno: (createAlunorData: CreateAlunoType) => Promise<CreateAlunoType | null>;
    getAlunoPaged: (
        pageNumber: number,
        contratoId: number,
        pageSize?: number,
        name?: string,
        idAluno?: number,
        cursoId?: number,
        referenceCode?: number,
        empresaId?: number
    ) => Promise<AlunoType[] | null>;
    updateAluno: (editAlunorData: EditAlunoType) => Promise<EditAlunoType | null>;
    deleteAluno: (alunoId: number, force?: boolean) => Promise<DeleteGuardResult>;
    handleGetAlunoByUserId: (alunoId: number) => Promise<AlunoType | null>;
    handleGetAlunoById: (alunoId: number) => Promise<AlunoType | null>;
    handleGetAlunoByReferenceCode: (referenceCode: number) => Promise<AlunoType | null>;

    copyTransferAlunos: (payload: CopyTransferAlunosRequest) => Promise<CopyTransferAlunosResponse | null>;
    removeAlunoFromContrato: (contratoId: number, alunoId: number, cursoId: number) => Promise<RemoveAlunoFromContratoResponse | null>;
    getAlunosSemVinculo: (
        pageNumber: number,
        empresaId: number,
        pageSize?: number,
        name?: string,
        referenceCode?: number
    ) => Promise<{ result: AlunoType[]; pageCount: number } | null>;
    revinculaAluno: (alunoId: number, contratoId: number, cursoId: number) => Promise<boolean>;
    alterarCursoAluno: (alunoId: number, contratoId: number, novoCursoId: number) => Promise<boolean>;

    setAluno: any;
    aluno: AlunoType;
    listAluno: AlunoType[];
    initialListAluno: AlunoType[];
    setListAluno: any;
    setAlunoPageCount: any;
    alunoPageCount: number;
    totalAlunos: number;
    setTotalAlunos: any;

    getAlunosDetalhadosPaged: (
        pageNumber: number,
        empresaId: number,
        pageSize?: number,
        name?: string,
        alunoId?: number,
        contratoId?: number,
        relatorioId?: number,
        albumId?: number,
        cidade?: string,
        cursoId?: number
    ) => Promise<AlunoDetalhadoType[] | null>;
    listAlunoDetalhado: AlunoDetalhadoType[];
    alunoDetalhadoPageCount: number;

    gerarRelatorioAlunos: (request: AlunoRelatorioRequest) => Promise<void>;
    validateReferenceCode: (referenceCode: number) => Promise<{ message: string; nome?: string; id?: number } | null>;
}

const initialState: AlunoContextData = {
    createAluno: async () => null,
    getAlunoPaged: async () => Promise.resolve(null),
    updateAluno: async () => null,
    deleteAluno: async () => ({ status: "error" } as DeleteGuardResult),
    handleGetAlunoByUserId: async () => null,
    handleGetAlunoById: async () => null,
    handleGetAlunoByReferenceCode: async () => null,

    copyTransferAlunos: async () => null,
    removeAlunoFromContrato: async () => null,
    getAlunosSemVinculo: async () => null,
    revinculaAluno: async () => false,
    alterarCursoAluno: async () => false,

    setAluno: () => { },
    aluno: {
        id: 0,
        referenceCode: 0,
        name: "",
        email: "",
        cpf: "",
        address: "",
        addressNumber: "",
        neighborhood: "",
        city: "",
        state: "",
        zipCode: "",
        landline: "",
        telephone_01: "",
        telephone_02: "",
        telephone_03: "",
        telephone_04: "",
        telephone_05: "",
        telephone_06: "",
        facebook: "",
        instagram: "",
        keepThePhotos: false,
        hasAlbum: false,
        numberOfPhotos: 0,
        cursoId: 0,
        cursoName: "",
        base64Image: "",
    },
    listAluno: [],
    initialListAluno: [],
    setListAluno: () => { },
    setAlunoPageCount: () => { },
    alunoPageCount: 0,
    totalAlunos: 0,
    setTotalAlunos: () => { },

    getAlunosDetalhadosPaged: async () => Promise.resolve(null),
    listAlunoDetalhado: [],
    alunoDetalhadoPageCount: 0,

    gerarRelatorioAlunos: async () => { },
    validateReferenceCode: async () => null,
};

export const AlunoContext = createContext<AlunoContextData>(initialState);

export default function AlunoContextProvider({ children }: AlunoContextProviderProps) {
    const {
        createAluno,
        getAlunoPaged,
        updateAluno,
        deleteAluno,
        handleGetAlunoByUserId,
        handleGetAlunoById,
        handleGetAlunoByReferenceCode,

        copyTransferAlunos,
        removeAlunoFromContrato,
        getAlunosSemVinculo,
        revinculaAluno,
        alterarCursoAluno,

        setAluno,
        aluno,
        listAluno,
        initialListAluno,
        setListAluno,
        alunoPageCount,
        setAlunoPageCount,
        totalAlunos,
        setTotalAlunos,
        getAlunosDetalhadosPaged,
        listAlunoDetalhado,
        alunoDetalhadoPageCount,
        gerarRelatorioAlunos,
        validateReferenceCode,
    } = useAluno();

    return (
        <AlunoContext.Provider
            value={{
                createAluno,
                getAlunoPaged,
                updateAluno,
                deleteAluno,
                handleGetAlunoByUserId,
                handleGetAlunoById,
                handleGetAlunoByReferenceCode,

                copyTransferAlunos,
                removeAlunoFromContrato,
                getAlunosSemVinculo,
                revinculaAluno,
                alterarCursoAluno,

                setAluno,
                aluno,
                listAluno,
                initialListAluno,
                setListAluno,
                alunoPageCount,
                setAlunoPageCount,
                totalAlunos,
                setTotalAlunos,
                getAlunosDetalhadosPaged,
                listAlunoDetalhado,
                alunoDetalhadoPageCount,
                gerarRelatorioAlunos,
                validateReferenceCode,
            }}
        >
            {children}
        </AlunoContext.Provider>
    );
}